import { Status } from '@df/core/status';
import { Address, IAddressData } from '@df/session/api/address-book/address';
import { Customer } from '@df/session/api/customer';
import { HistoryOrderItem, historyOrderItemFactory, IHistoryOrderItemData } from '@df/session/api/history/history-order-item';
import { OrderService } from '@df/session/api/order/order.service';
import { Nexus } from '@tomandco/nexus-api-types';
import map from 'lodash-es/map';
import reduce from 'lodash-es/reduce';

export interface IHistoryOrderDataPaymentMethod {
    description: string;
}

export interface IHistoryOrderData {
    id: number;
    items: IHistoryOrderItemData[];
    billing?: IAddressData;
    shipping?: IAddressData;
    number: string;
    date: string;
    status: string;
    totals?: IHistoryOrderTotals;
    payment_method?: IHistoryOrderDataPaymentMethod;
    shipping_method?: {
        description: string;
    };
    currency?: string;
    currency_symbol?: string;
    tracking?: { [number: string]: IHistoryTrackingItem };
}

export interface IHistoryOrderTotals extends Nexus.Lib.Basket.Totals {
    delivery?: number;
}

export interface IHistoryTrackingItem {
    number: string;
    title: string;
    url?: string;
    carrier?: string;
}

export class HistoryOrder {
    protected _data!: IHistoryOrderData;

    qty!: number;
    items!: HistoryOrderItem[];
    billing?: Address;
    shipping?: Address;

    constructor(
        data: IHistoryOrderData,
        private customer: Customer,
        private orderService: OrderService
    ) {
        this.data = data;
    }

    cancelStatus: Status = new Status();

    get id(): number {
        return this._data.id;
    }

    set data(data: IHistoryOrderData) {
        this.setData(data);
    }

    get data(): IHistoryOrderData {
        return this._data;
    }

    get canCancelByDate(): Date | boolean {
        return this.orderService.canCancelByDate(this.data.date);
    }

    get isCanceled(): boolean {
        const status = this.data.status.toLowerCase();
        return ['canceled', 'cancelled'].includes(status);
    }

    get isClosed(): boolean {
        return this.data.status.toLowerCase() === 'closed';
    }

    cancel = () => {
        this.orderService.cancel(this.cancelStatus, this.data.number, this.customer.model.data.email || '', result =>
            this.onCancelOrderSuccess(result)
        );
    };

    private onCancelOrderSuccess(result) {
        this.data = result.customer.order;
    }

    protected setData(data: IHistoryOrderData) {
        this.items = map(data.items, item => new historyOrderItemFactory.instance(item)) || [];
        this.billing = data.billing ? new Address(data.billing) : undefined;
        this.shipping = data.shipping ? new Address(data.shipping) : undefined;
        this.qty = reduce(this.items, (qty, item) => qty + item.data.qty_ordered, 0);
        this._data = data;
    }
}

export const historyOrderFactory = {
    instance: HistoryOrder
};
